// TaskFormFields — React Hook Form + Zod resolver + MUI TextField/Select.
// Поля: title + description + priority + assignee + dueDate (через DatePickerField) +
// обязательный «Департамент» (B1: POST /api/tasks требует @NotNull departmentId;
// U-20, sled-V9 W6: DepartmentAutocomplete — поисковый селект вместо 1258 MenuItem).
// R36b / U-2: исполнитель — UserAutocomplete (живой поиск /api/users/lookup), без ручного UUID.
import { Controller, useForm, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Stack, TextField, MenuItem, Button, Box } from '@mui/material';
import { DateTimePickerField } from '@/components/forms';
import { UserAutocomplete } from '@/components/UserAutocomplete';
import { DepartmentAutocomplete } from '@/components/DepartmentAutocomplete';
import { t } from '@/i18n';
import type { TaskPriority } from '@/api/tasks';

/** true если ISO Instant не задан или не в прошлом (U-21, zod-рефайн). */
function isFutureInstant(v: string | undefined): boolean {
  return !v || new Date(v).getTime() >= Date.now();
}

/** Zod-схема валидации формы задачи. */
const taskFormSchema = z.object({
  title: z.string().min(1, 'Название обязательно').max(200, 'Слишком длинное название'),
  description: z.string().max(2000, 'Слишком длинное описание').optional().or(z.literal('')),
  priority: z.enum(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']),
  assigneeUserId: z.string().optional().or(z.literal('')),
  // U-21: dueAt — ISO Instant (DateTimePickerField). Прошлый срок — zod-рефайн:
  // валидатор остаётся в форме независимо от ограничений пикера (ручной ввод проходит).
  dueAt: z.string().optional().or(z.literal('')).refine(isFutureInstant, t('tasks.dueAtPast')),
  // B1: backend TaskCreateRequest.departmentId — @NotNull; пусто → validation error.
  departmentId: z.string().min(1, t('tasks.departmentRequired')),
});

export type TaskFormValues = z.infer<typeof taskFormSchema>;

interface TaskFormFieldsProps {
  /** Значения по умолчанию (для редактирования). */
  defaultValues?: Partial<TaskFormValues>;
  /** Колбэк отправки формы. */
  onSubmit: SubmitHandler<TaskFormValues>;
  /** Флаг процесса отправки (блокирует кнопку). */
  submitting?: boolean;
  /** Текст кнопки отправки. */
  submitLabel?: string;
}

const PRIORITY_OPTIONS: Array<{ value: TaskPriority; label: string }> = [
  { value: 'LOW', label: 'Низкий' },
  { value: 'MEDIUM', label: 'Средний' },
  { value: 'HIGH', label: 'Высокий' },
  { value: 'CRITICAL', label: 'Критический' },
];

/** Поля формы создания/редактирования задачи. */
export function TaskFormFields({
  defaultValues,
  onSubmit,
  submitting = false,
  submitLabel = 'Сохранить',
}: TaskFormFieldsProps): JSX.Element {
  const { control, handleSubmit } = useForm<TaskFormValues>({
    resolver: zodResolver(taskFormSchema),
    defaultValues: {
      title: '',
      description: '',
      priority: 'MEDIUM',
      assigneeUserId: '',
      dueAt: '',
      departmentId: '',
      ...defaultValues,
    },
  });

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stack spacing={2}>
        <Controller
          name="title"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label="Название"
              required
              fullWidth
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
              inputProps={{ 'aria-label': 'Название задачи' }}
            />
          )}
        />
        <Controller
          name="description"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              value={field.value ?? ''}
              label="Описание"
              multiline
              rows={3}
              fullWidth
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
              inputProps={{ 'aria-label': 'Описание задачи' }}
            />
          )}
        />
        <Controller
          name="priority"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              select
              required
              fullWidth
              label="Приоритет"
              inputProps={{ 'aria-label': 'Приоритет задачи' }}
            >
              {PRIORITY_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
        <Controller
          name="assigneeUserId"
          control={control}
          render={({ field }) => (
            <UserAutocomplete
              label="Исполнитель (опц.)"
              value={field.value ?? ''}
              onChange={field.onChange}
              aria-label="Исполнитель"
            />
          )}
        />
        {/* B1 (обязателен) + U-20: поисковый селект департамента вместо 1258 MenuItem. */}
        <Controller
          name="departmentId"
          control={control}
          render={({ field, fieldState }) => (
            <DepartmentAutocomplete
              label={t('tasks.department')}
              value={field.value}
              onChange={field.onChange}
              required
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message ?? undefined}
              aria-label={t('tasks.department')}
            />
          )}
        />
        {/* U-21: dueAt — DateTimePickerField (ISO Instant, minDateTime='now');
            прежний DatePickerField отдавал yyyy-MM-dd → backend 400 JSON parse error. */}
        <DateTimePickerField
          name="dueAt"
          control={control}
          label="Срок выполнения"
          helperText="Дедлайн выполнения"
          minDateTime="now"
          aria-label="Срок выполнения"
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          {/* B1: submit всегда доступен — валидацию (включая департамент) показывает RHF. */}
          <Button type="submit" variant="contained" disabled={submitting}>
            {submitLabel}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
